import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../services/axiosConfig';
import { API_ENDPOINTS } from '../../config/api';
import { ESIM_SALE_KEY } from './esimOrderState';
import { detailTiles, formatInr, formatPackData, tagSpeed } from './esimPlanFormat';
import { EsimCompare } from './EsimCompare';
import { EsimOrdersSection } from './EsimOrdersSection';
import { CompatibilityDialog, type CompatTab } from './CompatibilityDialog';
import { trackEsimEvent } from './esimAnalytics';
import './CanadaEsimPage.css';

function rememberSale(saleId: string) {
  sessionStorage.setItem(ESIM_SALE_KEY, saleId);
}

interface CanadaPack {
  packageCode: string;
  name: string;
  dataGb: number;
  dataMb?: number | null;
  validityDays: number;
  priceInr: number;
  coverage?: string;
  speed?: string;
  network?: string;
  networkType?: string;
  operators?: Array<{ name: string; type?: string | null }>;
  topUp?: boolean | null;
  hotspot?: boolean | null;
  activation?: string | null;
  activateBy?: string | null;
  ipLocation?: string | null;
  maxPrivacy?: boolean | null;
  sms?: string | null;
  dataLabel?: string | null;
  durationLabel?: string | null;
  showData?: boolean;
  showDuration?: boolean;
  detailsResolved?: boolean;
}

interface CanadaPass {
  available: boolean;
  priceInr: number | null;
  name?: string;
  dataGb?: number;
  dataMb?: number | null;
  validityDays?: number;
  packageCode?: string;
  packs?: CanadaPack[];
  coverage?: string;
  speed?: string;
  network?: string;
  topUp?: boolean | null;
  reason?: string;
}

interface CheckoutResponse {
  saleId: string;
  razorpayOrderId: string;
  keyId: string;
  amountPaise: number;
  currency: 'INR';
}

interface RazorpaySuccess {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

interface RazorpayCheckout {
  open: () => void;
  on?: (event: string, handler: () => void) => void;
}

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => RazorpayCheckout;
  }
}

type ListedPack = CanadaPack & { country: 'CA' | 'IN' };

function listedPacks(pass: CanadaPass | null, country: 'CA' | 'IN'): ListedPack[] {
  return packsFrom(pass).map((pack) => ({ ...pack, country }));
}

function catalogFailure(error: unknown): string {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 401) return 'Sign in again to see eSIM packs.';
  if (status == null) return 'The eSIM service is unreachable.';
  return 'Unable to load eSIM plans.';
}

function checkoutError(error: unknown): string {
  const response = (error as { response?: { status?: number; data?: { error?: string } } })?.response;
  const message = response?.data?.error;
  if (response?.status && response.status < 500 && message) return message;
  return 'Checkout could not be started.';
}

function packsFrom(pass: CanadaPass | null): CanadaPack[] {
  if (!pass) return [];
  if (Array.isArray(pass.packs) && pass.packs.length > 0) return pass.packs;
  if (pass.available) {
    return [{
      packageCode: pass.packageCode || '',
      name: pass.name || 'eSIM',
      dataGb: pass.dataGb || 0,
      dataMb: pass.dataMb,
      validityDays: pass.validityDays || 0,
      priceInr: pass.priceInr || 0,
      coverage: pass.coverage,
      speed: pass.speed,
      network: pass.network,
      topUp: pass.topUp,
    }];
  }
  return [];
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function SignalIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
      <path d="M4 20v-3M9 20v-7M14 20V9M19 20V4" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8h.01M11 12h1v5h1" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function loadRazorpay(): Promise<void> {
  if (window.Razorpay) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Razorpay failed to load'));
    document.body.appendChild(script);
  });
}

export const CanadaEsimPage: React.FC = () => {
  const plansPath = '/store/canada-esim';
  const navigate = useNavigate();
  const [canadaPacks, setCanadaPacks] = useState<ListedPack[]>([]);
  const [indiaPacks, setIndiaPacks] = useState<ListedPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [payingCode, setPayingCode] = useState<string | null>(null);
  const [payError, setPayError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ kind: 'cancelled' | 'failed'; pack: CanadaPack } | null>(null);
  const [detailPack, setDetailPack] = useState<ListedPack | null>(null);
  const [orderCount, setOrderCount] = useState(0);
  const [compatOpen, setCompatOpen] = useState(false);
  const [compatTab, setCompatTab] = useState<CompatTab>('check');
  const [sheetClosing, setSheetClosing] = useState(false);
  const paying = useRef(false);
  const pageRef = useRef<HTMLElement>(null);
  const openerRef = useRef<HTMLButtonElement | null>(null);
  const sheetRef = useRef<HTMLElement>(null);

  const loadCatalog = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    setCanadaPacks([]);
    setIndiaPacks([]);
    Promise.allSettled([
      axiosInstance.get(API_ENDPOINTS.ESIM_CANADA),
      axiosInstance.get(API_ENDPOINTS.ESIM_INDIA),
    ]).then(([canada, india]) => {
      const canadaList = canada.status === 'fulfilled' ? listedPacks(canada.value?.data ?? null, 'CA') : [];
      const indiaList = india.status === 'fulfilled' ? listedPacks(india.value?.data ?? null, 'IN') : [];
      setCanadaPacks(canadaList);
      setIndiaPacks(indiaList);
      if (canadaList.length === 0 && indiaList.length === 0 && canada.status === 'rejected' && india.status === 'rejected') {
        setLoadError(catalogFailure(canada.reason));
      }
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const closeDetails = useCallback(() => {
    const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setDetailPack(null);
      setSheetClosing(false);
      openerRef.current?.focus();
      return;
    }
    setSheetClosing(true);
  }, []);

  useEffect(() => {
    if (!sheetClosing || !detailPack) return;
    const count = detailTiles(detailPack).length + (detailPack.network?.trim() ? 1 : 0);
    const timer = window.setTimeout(() => {
      setDetailPack(null);
      setSheetClosing(false);
      openerRef.current?.focus();
    }, Math.min(980, count * 70 + 280));
    return () => window.clearTimeout(timer);
  }, [sheetClosing, detailPack]);

  useEffect(() => {
    const page = pageRef.current;
    if (!detailPack) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (page) page.setAttribute('inert', '');
    const sheet = sheetRef.current;
    const focusTimer = window.setTimeout(() => sheet?.focus(), 0);

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDetails();
        return;
      }
      if (event.key !== 'Tab' || !sheetRef.current) return;
      const focusable = [...sheetRef.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea')]
        .filter((node) => !node.hasAttribute('disabled'));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      page?.removeAttribute('inert');
      window.clearTimeout(focusTimer);
      window.removeEventListener('keydown', onKey);
    };
  }, [detailPack, closeDetails]);

  const pay = async (pack: ListedPack) => {
    if (paying.current) return;
    paying.current = true;
    setPayingCode(pack.packageCode);
    setPayError(null);
    setNotice(null);
    try {
      const { data } = await axiosInstance.post<CheckoutResponse>(API_ENDPOINTS.ESIM_CHECKOUT, {
        packageCode: pack.packageCode,
        country: pack.country,
      });
      rememberSale(data.saleId);
      await loadRazorpay();
      if (!window.Razorpay) throw new Error('Razorpay failed to load');
      let settled = false;
      const stopOpening = () => {
        paying.current = false;
        setPayingCode(null);
      };
      const checkout = new window.Razorpay({
        key: data.keyId,
        order_id: data.razorpayOrderId,
        amount: data.amountPaise,
        currency: 'INR',
        name: 'Canadawale Yar',
        description: pack.name || 'eSIM',
        handler: async (response: RazorpaySuccess) => {
          settled = true;
          setNotice(null);
          setDetailPack(null);
          try {
            await axiosInstance.post(API_ENDPOINTS.ESIM_VERIFY, {
              saleId: data.saleId,
              ...response,
            });
            rememberSale(data.saleId);
            navigate(`${plansPath}/orders/${data.saleId}`);
          } catch {
            rememberSale(data.saleId);
            navigate(`${plansPath}/orders/${data.saleId}`);
          } finally {
            stopOpening();
          }
        },
        modal: {
          ondismiss: () => {
            if (settled) return;
            settled = true;
            stopOpening();
            setDetailPack(null);
            sessionStorage.removeItem(ESIM_SALE_KEY);
            void axiosInstance.post(API_ENDPOINTS.ESIM_CHECKOUT_ABANDON, { saleId: data.saleId }).catch(() => undefined);
            setNotice({ kind: 'cancelled', pack });
          },
        },
      });
      checkout.on?.('payment.failed', () => {
        if (settled) return;
        settled = true;
        stopOpening();
        setDetailPack(null);
        sessionStorage.removeItem(ESIM_SALE_KEY);
        void axiosInstance.post(API_ENDPOINTS.ESIM_CHECKOUT_FAILED, { saleId: data.saleId }).catch(() => undefined);
        setNotice({ kind: 'failed', pack });
      });
      setDetailPack(null);
      checkout.open();
    } catch (error) {
      setPayError(checkoutError(error));
      paying.current = false;
      setPayingCode(null);
    }
  };

  const backToPlans = () => {
    setNotice(null);
    setPayError(null);
    paying.current = false;
    setPayingCode(null);
  };

  const openDetails = (pack: ListedPack, opener: HTMLButtonElement) => {
    openerRef.current = opener;
    setSheetClosing(false);
    setDetailPack(pack);
  };

  const packs = [...canadaPacks, ...indiaPacks];
  const priceLabel = formatInr(detailPack?.priceInr);
  const tiles = detailPack ? detailTiles(detailPack) : [];
  const networkName = detailPack?.network?.trim() || '';
  const operators = detailPack?.operators?.filter((operator) => operator.name?.trim()) ?? [];
  const operatorCards = operators.length > 0
    ? operators
    : networkName
      ? [{ name: networkName, type: detailPack?.networkType }]
      : [];

  const sheet = detailPack ? createPortal(
    <div className={sheetClosing ? 'cwy-esim-theme esim-scrim is-closing' : 'cwy-esim-theme esim-scrim'} onClick={(event) => { if (event.target === event.currentTarget) closeDetails(); }}>
      <section
        className={sheetClosing ? 'esim-sheet is-closing' : 'esim-sheet'}
        style={{ ['--esim-out' as string]: `${(tiles.length + (networkName ? 1 : 0)) * 70}ms` }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="esim-detail-title"
        tabIndex={-1}
        ref={sheetRef}
      >
        <div className="esim-sheet-head">
          <div>
            <p className="esim-eyebrow">Plan details</p>
            <h2 id="esim-detail-title">{detailPack.name}</h2>
          </div>
          <button type="button" className="esim-x" aria-label="Close" onClick={closeDetails}><CloseIcon /></button>
        </div>
        <div className="esim-hero">
          <div>
            {detailPack.showData !== false ? (
              <p className="esim-hero-data">
                {detailPack.dataLabel?.trim() ? (
                  detailPack.dataLabel
                ) : (
                  <>
                    {formatPackData(detailPack).value}
                    <span>{formatPackData(detailPack).unit}</span>
                  </>
                )}
              </p>
            ) : null}
            {detailPack.showDuration !== false || detailPack.speed ? (
              <p className="esim-hero-sub">
                {detailPack.showDuration !== false
                  ? `Valid for ${detailPack.durationLabel?.trim() || `${detailPack.validityDays} days`}`
                  : ''}
                {detailPack.showDuration !== false && detailPack.speed ? ' · ' : ''}
                {detailPack.speed || ''}
              </p>
            ) : null}
          </div>
          <p className="esim-hero-price">{priceLabel}<small>one-time</small></p>
        </div>
        {tiles.length > 0 ? (
          <dl className="esim-grid">
            {tiles.map((tile, index) => (
              <div
                key={tile.label}
                className={tile.span ? 'esim-tile is-span esim-fact' : 'esim-tile esim-fact'}
                style={{ animationDelay: `${(sheetClosing ? tiles.length - index : index) * 70}ms` }}
              >
                <dt>{tile.label}</dt>
                <dd>
                  {tile.value}
                  {tile.chip ? <span className="esim-privacy">{tile.chip}</span> : null}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}
        {operatorCards.length > 0 ? (
          <div className="esim-ops esim-fact" style={{ animationDelay: `${(sheetClosing ? 0 : tiles.length) * 70}ms` }}>
            <h3>Network operators</h3>
            <div className="esim-op-grid">
              {operatorCards.map((operator) => (
                <div className="esim-op" key={operator.name}>
                  <b>{operator.name}</b>
                  {operator.type ? <span>{operator.type}</span> : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}
        <div className="esim-actions">
          <button type="button" className="esim-btn esim-btn-ghost" onClick={closeDetails}>Close</button>
          <button type="button" className="esim-btn" onClick={() => { void pay(detailPack); }} disabled={payingCode !== null}>
            {payingCode === detailPack.packageCode ? 'Opening checkout…' : 'Pay'}
          </button>
        </div>
      </section>
    </div>,
    document.body,
  ) : null;

  if (notice) {
    const cancelled = notice.kind === 'cancelled';
    return (
      <main className="cwy-esim-theme canada-esim" ref={pageRef}>
        <div className="esim-wrap">
          <header className="esim-top">
            <p className="esim-eyebrow">Travel data</p>
            <h1>{cancelled ? 'Payment cancelled' : 'Payment did not go through'}</h1>
            <p className="esim-lead">
              {cancelled ? 'You have not been charged.' : 'Nothing was taken from your account.'}
            </p>
          </header>
          <div className="esim-result">
            <div className="esim-result-pack">
              <p className="esim-result-name">{notice.pack.name}</p>
              <p>
                {formatPackData(notice.pack).value} {formatPackData(notice.pack).unit}
                {notice.pack.validityDays ? ` · ${notice.pack.validityDays} days` : ''}
              </p>
              <p>{formatInr(notice.pack.priceInr)}</p>
            </div>
            <button type="button" className="esim-btn" onClick={backToPlans}>
              {cancelled ? 'Back to plans' : 'Try again'}
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="cwy-esim-theme canada-esim" ref={pageRef}>
      <div className="esim-wrap">
      <header className="esim-top">
        <p className="esim-eyebrow">Travel data</p>
        <h1>eSIM</h1>
        <p className="esim-lead">Pick a data pack. Pay in INR. The QR arrives after payment.</p>
        <div className="esim-compat-row-trigger">
          <button
            type="button"
            className="esim-btn esim-btn-ghost"
            onClick={() => {
              setCompatTab('check');
              setCompatOpen(true);
              trackEsimEvent({ name: 'compat_opened', source: 'header' });
            }}
          >
            Check compatibility
          </button>
          <span className="esim-compat-aside">Takes 10 seconds. Dial *#06# on your phone.</span>
        </div>
        {orderCount > 0 ? <a className="esim-orders-jump" href="#your-esims">Your eSIMs ({orderCount})</a> : null}
      </header>
      {loading ? (
        <div className="esim-plans" aria-hidden="true">
          <div className="esim-plan esim-skeleton" />
          <div className="esim-plan esim-skeleton" />
          <div className="esim-plan esim-skeleton" />
        </div>
      ) : null}
      {loadError ? (
        <div className="esim-error" role="alert">
          <p>{loadError}</p>
          <button type="button" onClick={loadCatalog}>Retry</button>
        </div>
      ) : null}
      {!loading && !loadError && packs.length === 0 ? (
        <section className="esim-empty" aria-live="polite">
          <span className="esim-empty-mark" aria-hidden="true">
            <SignalIcon />
          </span>
          <div>
            <h2>No plan is active right now</h2>
            <p>Canada and India packs show up here once they are turned on. Check back in a little while.</p>
          </div>
        </section>
      ) : null}
      {!loading && packs.length > 0 ? (
        <>
          {[
            ['Canada', canadaPacks],
            ['India', indiaPacks],
          ].filter((entry): entry is ['Canada' | 'India', ListedPack[]] => entry[1].length > 0).map(([label, sectionPacks]) => (
            <section key={label} className="esim-section" aria-label={`${label} plans`}>
              <h2>{label}</h2>
              <div className="esim-plans">
                {sectionPacks.map((pack) => {
                  const figure = formatPackData(pack);
                  return (
                    <article key={`${pack.country}-${pack.packageCode || pack.name}`} className="esim-plan">
                      <button
                        type="button"
                        className="esim-details"
                        aria-label={`View details for ${pack.name}`}
                        onClick={(event) => openDetails(pack, event.currentTarget)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openDetails(pack, event.currentTarget);
                          }
                        }}
                      >
                        <span className="esim-plan-main">
                          <span className="esim-plan-data">{figure.value}<span>{figure.unit}</span></span>
                          <span className="esim-plan-name">{pack.name}</span>
                        </span>
                        <span className="esim-chips">
                          <span className="esim-chip"><ClockIcon />{pack.validityDays} days</span>
                          {tagSpeed(pack) ? <span className="esim-chip esim-chip-net"><SignalIcon />{tagSpeed(pack)}</span> : null}
                        </span>
                      </button>
                      <div className="esim-buy">
                        <p className="esim-price"><b>{formatInr(pack.priceInr)}</b><small>one-time</small></p>
                        <button type="button" className="esim-btn" onClick={() => { void pay(pack); }} disabled={payingCode !== null}>
                          {payingCode === pack.packageCode ? 'Opening checkout…' : 'Pay'}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
          <p className="esim-hint"><InfoIcon />Tap a plan to see full details</p>
          <EsimOrdersSection onCount={setOrderCount} />
          {payError ? <p role="alert">{payError}</p> : null}
          <ul className="esim-notes">
            <li>Install on Wi-Fi before you fly.</li>
            <li>Your phone must be unlocked and eSIM-capable. <button type="button" className="esim-compat-note-link" onClick={() => { setCompatTab('check'); setCompatOpen(true); trackEsimEvent({ name: 'compat_opened', source: 'notes' }); }}>Check yours</button></li>
            <li>Data starts on the first connection in the plan country.</li>
          </ul>
        </>
      ) : null}
      </div>
      {packs.length > 0 ? (
        <div className="esim-compare-wrap">
          <EsimCompare />
          <p className="esim-legal">Sold by Canadawale Yar. Network service is provided by the carrier partner.</p>
        </div>
      ) : null}
      {sheet}
      <CompatibilityDialog open={compatOpen} tab={compatTab} onClose={() => setCompatOpen(false)} onTab={setCompatTab} />
    </main>
  );
};
